'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Inbox,
  Tag,
  Workflow,
  MessageSquare,
  LineChart,
  Sparkles,
  ShieldCheck,
  Zap,
  Bot,
  Check,
} from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */
function ChannelsToInboxBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.7 }} aria-hidden="true">
      <defs>
        <radialGradient id="ci-glow" cx="50%" cy="60%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.14" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#ci-glow)" />
      {[{ x: 80, y: 90 }, { x: 200, y: 60 }, { x: 320, y: 90 }].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="14" fill={tint.accent} fillOpacity="0.10" />
          <circle cx={p.x} cy={p.y} r="7" fill={tint.accent} fillOpacity="0.85" />
        </g>
      ))}
      <path d="M 80 104 Q 130 200, 200 250" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      <path d="M 200 74 L 200 250" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      <path d="M 320 104 Q 270 200, 200 250" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      <rect x="135" y="250" width="130" height="80" rx="10" fill={tint.accent} fillOpacity="0.10" stroke={tint.accent} strokeOpacity="0.4" />
      <line x1="155" y1="278" x2="245" y2="278" stroke={tint.accent} strokeOpacity="0.45" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="155" y1="296" x2="225" y2="296" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="155" y1="312" x2="235" y2="312" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function TagBinsBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.72 }} aria-hidden="true">
      <defs>
        <radialGradient id="tb-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#tb-glow)" />
      {[{ y: 70, w: 110, op: 0.5 }, { y: 86, w: 100, op: 0.7 }, { y: 102, w: 120, op: 0.95 }].map((tk, i) => (
        <rect key={i} x="40" y={tk.y} width={tk.w} height="12" rx="3" fill={tint.accent} fillOpacity={tk.op * 0.45} />
      ))}
      <path d="M 165 90 Q 220 90, 240 150" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      <path d="M 165 100 Q 230 130, 290 200" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      <path d="M 165 110 Q 200 220, 200 290" fill="none" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.3" strokeDasharray="4 4" />
      {[{ x: 215, y: 140, label: 'Sentiment' }, { x: 260, y: 190, label: 'Intent' }, { x: 165, y: 280, label: 'Theme' }].map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width="100" height="36" rx="8" fill="#fff" stroke={tint.accent} strokeOpacity="0.5" />
          <rect x={b.x + 8} y={b.y + 10} width="10" height="16" rx="2" fill={tint.accent} fillOpacity="0.75" />
          <line x1={b.x + 26} y1={b.y + 14} x2={b.x + 88} y2={b.y + 14} stroke={tint.accent} strokeOpacity="0.45" strokeWidth="2" strokeLinecap="round" />
          <line x1={b.x + 26} y1={b.y + 22} x2={b.x + 70} y2={b.y + 22} stroke={tint.accent} strokeOpacity="0.25" strokeWidth="2" strokeLinecap="round" />
        </g>
      ))}
    </svg>
  );
}

function WorkflowFlowBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.72 }} aria-hidden="true">
      <defs>
        <radialGradient id="wf-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
        <marker id="wf-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={tint.accent} fillOpacity="0.55" />
        </marker>
      </defs>
      <rect width="400" height="400" fill="url(#wf-glow)" />
      <rect x="50" y="180" width="60" height="40" rx="8" fill={tint.accent} fillOpacity="0.18" stroke={tint.accent} strokeOpacity="0.45" />
      <rect x="170" y="90"  width="60" height="40" rx="8" fill="#fff" stroke={tint.accent} strokeOpacity="0.4" />
      <rect x="170" y="180" width="60" height="40" rx="8" fill="#fff" stroke={tint.accent} strokeOpacity="0.4" />
      <rect x="170" y="270" width="60" height="40" rx="8" fill="#fff" stroke={tint.accent} strokeOpacity="0.4" />
      <rect x="290" y="180" width="60" height="40" rx="8" fill={tint.accent} fillOpacity="0.18" stroke={tint.accent} strokeOpacity="0.45" />
      <line x1="115" y1="200" x2="165" y2="110" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="115" y1="200" x2="165" y2="200" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="115" y1="200" x2="165" y2="290" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="235" y1="110" x2="285" y2="195" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="235" y1="200" x2="285" y2="200" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="235" y1="290" x2="285" y2="210" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1.5" markerEnd="url(#wf-arrow)" />
      <line x1="178" y1="105" x2="222" y2="105" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />
      <line x1="178" y1="195" x2="222" y2="195" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />
      <line x1="178" y1="285" x2="222" y2="285" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChatBubblesBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.72 }} aria-hidden="true">
      <defs>
        <radialGradient id="cb-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#cb-glow)" />
      <path
        d="M 50 100 Q 50 88, 62 88 L 200 88 Q 212 88, 212 100 L 212 140 Q 212 152, 200 152 L 90 152 L 70 168 L 75 152 L 62 152 Q 50 152, 50 140 Z"
        fill="#fff" stroke={tint.accent} strokeOpacity="0.4"
      />
      <line x1="68" y1="108" x2="190" y2="108" stroke={tint.accent} strokeOpacity="0.45" strokeWidth="2" strokeLinecap="round" />
      <line x1="68" y1="124" x2="170" y2="124" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="2" strokeLinecap="round" />
      <line x1="68" y1="140" x2="150" y2="140" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M 188 200 Q 188 188, 200 188 L 350 188 Q 362 188, 362 200 L 362 260 Q 362 272, 350 272 L 220 272 L 215 288 L 210 272 L 200 272 Q 188 272, 188 260 Z"
        fill={tint.accent} fillOpacity="0.18" stroke={tint.accent} strokeOpacity="0.45"
      />
      <line x1="205" y1="210" x2="345" y2="210" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="205" y1="228" x2="325" y2="228" stroke={tint.accent} strokeOpacity="0.40" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="205" y1="246" x2="335" y2="246" stroke={tint.accent} strokeOpacity="0.40" strokeWidth="2.2" strokeLinecap="round" />
      <line x1="205" y1="262" x2="290" y2="262" stroke={tint.accent} strokeOpacity="0.40" strokeWidth="2.2" strokeLinecap="round" />
      {[{ x: 350, y: 175, s: 6 }, { x: 370, y: 195, s: 4 }, { x: 335, y: 165, s: 3 }].map((p, i) => (
        <g key={i} transform={`translate(${p.x},${p.y})`}>
          <path d={`M 0 ${-p.s} L ${p.s * 0.3} ${-p.s * 0.3} L ${p.s} 0 L ${p.s * 0.3} ${p.s * 0.3} L 0 ${p.s} L ${-p.s * 0.3} ${p.s * 0.3} L ${-p.s} 0 L ${-p.s * 0.3} ${-p.s * 0.3} Z`} fill={tint.accent} fillOpacity="0.65" />
        </g>
      ))}
    </svg>
  );
}

function DashboardChartBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.75 }} aria-hidden="true">
      <defs>
        <radialGradient id="dc-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="dc-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.30" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="400" height="400" fill="url(#dc-glow)" />
      {[{ x: 40 }, { x: 140 }, { x: 240 }].map((tk, i) => (
        <g key={i}>
          <rect x={tk.x} y="60" width="80" height="46" rx="8" fill="#fff" stroke={tint.accent} strokeOpacity="0.35" />
          <rect x={tk.x + 12} y="72" width="32" height="6" rx="2" fill={tint.accent} fillOpacity="0.30" />
          <rect x={tk.x + 12} y="86" width="50" height="10" rx="2" fill={tint.accent} fillOpacity="0.65" />
        </g>
      ))}
      <line x1="40" y1="320" x2="360" y2="320" stroke={tint.accent} strokeOpacity="0.25" />
      {[260, 220, 180].map((y) => (
        <line key={y} x1="40" y1={y} x2="360" y2={y} stroke={tint.accent} strokeOpacity="0.08" />
      ))}
      <path
        d="M 40 280 L 90 260 L 140 270 L 190 235 L 240 220 L 290 190 L 360 160 L 360 320 L 40 320 Z"
        fill="url(#dc-area)"
      />
      <path
        d="M 40 280 L 90 260 L 140 270 L 190 235 L 240 220 L 290 190 L 360 160"
        fill="none" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="2.2"
        strokeLinejoin="round" strokeLinecap="round"
      />
      {[{ x: 90, y: 260 }, { x: 190, y: 235 }, { x: 290, y: 190 }, { x: 360, y: 160 }].map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r="3.5" fill={tint.accent} />
      ))}
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Holistic Digital Care',
  tagline: 'The World Is Watching. Is Your Brand Responding With Intent?',
  body: [
    'Your brand\'s reputation is shaped in real time. Every tag, tweet, and touchpoint counts. Locobuzz helps you detect sentiment, route tickets smartly, and respond with precision.',
    'It\'s not just conversation management — it\'s holistic Digital Care, re-mastered.',
  ],
};

/* DRAFT — review: Impact band — proof-point numbers for Digital Care. */
const IMPACT = [
  /* DRAFT — review */ { value: '10B+', label: 'Conversations monitored annually' },
  /* DRAFT — review */ { value: '200+', label: 'Languages supported natively' },
  /* DRAFT — review */ { value: '40%', label: 'Faster ticket resolution with AI' },
  /* DRAFT — review */ { value: '99%', label: 'SLA compliance with smart routing' },
];

const STACK_POINTS = [
  { Icon: Inbox,       title: 'Real-Time Mentions',    text: 'Track every customer mention in real time across Instagram, Google, Meta, X, and more.', tint: 'blue' },
  { Icon: Sparkles,    title: 'Enriched Tickets',       text: 'Enrich tickets with customer data and generate instant summaries for faster context.',    tint: 'violet' },
  { Icon: ShieldCheck, title: 'AgentIQ™ Insights',      text: 'Predict CSAT and coach teams better with quality insights from AgentIQ™.',               tint: 'sky' },
  { Icon: Zap,         title: 'Early Sentiment Alerts', text: 'Catch negative sentiment early with alerts, smart categorisation, and auto-tagging.',    tint: 'blue' },
  { Icon: Workflow,    title: 'Intelligent Workflows',  text: 'Route tickets, trigger escalations, and automate SLAs with intelligent workflows.',      tint: 'violet' },
  { Icon: Bot,         title: 'ResponseGenie™',         text: 'Send brand-right, contextual replies at scale with ResponseGenie™ across every channel.', tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Monitor Mentions',
    title: 'Monitor Every Mention Across Every Channel',
    lead: 'Capture every brand mention across channels in real time and turn it into action.',
    bullets: [
      'Capture conversations from social, digital platforms in one inbox',
      'Filter by location, priority, or channel for focused response',
      'Turn unstructured mentions into searchable, actionable tickets',
      'Stay ahead of issues with live visibility across teams and markets',
    ],
    cta: { label: 'See It Live', href: '#book-demo' },
    visual: { Icon: Inbox, title: 'Unified Inbox', subtitle: 'Every Channel, One View', Bg: ChannelsToInboxBg, tint: 'blue' },
  },
  {
    eyebrow: 'Auto-Classify Tickets',
    title: 'Auto-Classify Tickets at Source',
    lead: 'Understand the nature, urgency, and impact of every ticket — automatically.',
    bullets: [
      'Enrich tickets with location, language, sentiment, and influence level',
      'Detect customer intent using AI for smarter prioritization',
      'Group feedback into themes to uncover customer trends quickly',
      'Free up agents from guesswork and manual sorting forever',
    ],
    visual: { Icon: Tag, title: 'Smart Tagging', subtitle: 'Sentiment · Intent · Theme', Bg: TagBinsBg, tint: 'violet' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'Manage Workflows',
    title: 'Manage Workflows Without the Bottlenecks',
    lead: 'Control the complete ticket lifecycle with rules, automation, and intelligence.',
    bullets: [
      'Route tickets based on channel, type, urgency, or region',
      'Automate approvals and assignments to reduce bottlenecks',
      'Customize workflows by team, brand, or use case easily',
      'Use AgentIQ™ to spot gaps and compliance risks automatically',
    ],
    visual: { Icon: Workflow, title: 'Routing Engine', subtitle: 'Rules · Automations · SLAs', Bg: WorkflowFlowBg, tint: 'sky' },
  },
  {
    eyebrow: 'Respond with AI',
    title: 'Respond With AI That Sounds Like Your Brand',
    lead: 'Craft replies that are accurate, human, and always on-brand — at any scale.',
    bullets: [
      'Auto-generate replies with ResponseGenie™ using context and tone',
      'Align responses with brand voice across 200+ languages',
      'Scale Digital Care while keeping every message personalized',
      'Deliver consistent, high-quality replies even under high volume',
    ],
    cta: { label: 'Improve Brand Reputation', href: '#book-demo' },
    visual: { Icon: MessageSquare, title: 'ResponseGenie™', subtitle: 'AI Replies, Brand-Right', Bg: ChatBubblesBg, tint: 'ice' },
    reverse: true,
  },
  {
    eyebrow: 'Track & Improve Performance',
    title: 'Track Performance, Coach Teams, Lift the Brand',
    lead: 'Use insights to coach teams, optimize processes, and elevate your reputation.',
    bullets: [
      'Predict CSAT scores using AgentIQ™ with every ticket',
      'Track TAT, FTR, SLA breaches, and agent performance in detail',
      'Break down trends by location, category, and issue type',
      'Turn performance data into action across the entire care engine',
    ],
    visual: { Icon: LineChart, title: 'Care Dashboards', subtitle: 'CSAT · TAT · FTR · SLA', Bg: DashboardChartBg, tint: 'blue' },
  },
];

/* DRAFT — review: FAQ copy derived from this page’s content; refine before rollout. */
const FAQS = [
  /* DRAFT — review */ { q: 'What is Holistic Digital Care?', a: 'Holistic Digital Care is Locobuzz\'s unified approach to managing every brand interaction — from social mentions to support tickets — in one intelligent platform. It combines real-time listening, AI-powered classification, smart routing, and automated response to deliver consistent, high-quality care at scale.' },
  /* DRAFT — review */ { q: 'Which channels does the unified inbox support?', a: 'Locobuzz\'s unified inbox captures conversations from Instagram, X (Twitter), Facebook, Google Reviews, Meta, WhatsApp, app stores, and more — giving your team one view across every digital touchpoint, in 200+ languages.' },
  /* DRAFT — review */ { q: 'How does AI-powered ticket routing work?', a: 'AgentIQ™ analyses each incoming ticket for channel, sentiment, urgency, language, and customer influence, then routes it automatically to the right agent or team — reducing manual triage and cutting resolution time by up to 40%.' },
  /* DRAFT — review */ { q: 'What is ResponseGenie™?', a: 'ResponseGenie™ is Locobuzz\'s AI reply engine. It generates contextually accurate, brand-right responses in the correct tone and language — letting agents review and send at scale without losing the human touch.' },
  /* DRAFT — review */ { q: 'Can I track SLA compliance and agent performance?', a: 'Yes. The Care Dashboards surface TAT, FTR, SLA breaches, and predicted CSAT scores per agent, team, or category — so managers can coach proactively and keep service standards consistently high.' },
];

/* DRAFT — review: Care Inbox panel — digital-care themed live dashboard. */
const PANEL = {
  title: 'Care Inbox',                      /* DRAFT — review */
  badge: 'Omni-channel',                    /* DRAFT — review */
  kpiValue: 3847,                           /* DRAFT — review */
  kpiLabel: 'Open conversations today',     /* DRAFT — review */
  trend: [8, 14, 11, 18, 22, 19, 28, 25, 36, 44], /* DRAFT — review */
  trendDelta: '+38%',                       /* DRAFT — review */
  segments: [                               /* DRAFT — review */
    { label: 'Resolved', pct: 62, tone: 'pos' },
    { label: 'Pending',  pct: 26, tone: 'neu' },
    { label: 'Breached', pct: 12, tone: 'neg' },
  ],
  feedLabel: 'Incoming queries',            /* DRAFT — review */
  feed: [                                   /* DRAFT — review */
    { text: 'My order hasn\'t arrived — it\'s been 5 days 😤', meta: '@riya_m', tag: 'X', tone: 'neg' },
    { text: 'Loving the new app update, so much faster!', meta: '@dev_patel', tag: 'Instagram', tone: 'pos' },
    { text: 'Can someone check my refund status?', meta: 'Google Reviews', tag: 'Google', tone: 'neu' },
    { text: 'The agent resolved my issue in minutes 🙌', meta: '@sarah.t', tag: 'Facebook', tone: 'pos' },
    { text: 'Product description doesn\'t match what I received', meta: '@krishna.v', tag: 'Instagram', tone: 'neg' },
  ],
  chip: 'AgentIQ™ · routed in 2s',         /* DRAFT — review */
};

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function HolisticDigitalCarePage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="hdc-hero-orbs">
          <span className="hdc-orb hdc-orb1" />
          <span className="hdc-orb hdc-orb2" />
        </div>
        <InlineStyle css={`
          .hdc-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .hdc-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .hdc-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:hdcOrb1 22s ease-in-out infinite; }
          .hdc-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:hdcOrb2 26s ease-in-out infinite; }
          @keyframes hdcOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes hdcOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .hdc-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Holistic Digital Care')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Holistic Digital Care')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Holistic Digital')} <span className="serif-accent italic">{t('Care')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE CARE EDGE ──────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('The Care Edge')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Digital Care Edge High-Performing Brands')} <span className="serif-accent italic">{t('Count On')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 760, margin: '0 auto', lineHeight: 1.7 }}>{t('Protect and scale enterprise brand equity with Locobuzz\'s AI-powered Digital Care platform. Proactively monitor sentiment, classify tickets at source, and respond at scale — so every interaction becomes a brand-building moment.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('What you get')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Full-Stack Behind')} <span className="serif-accent italic">{t('Smarter Digital Care')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 720, margin: '0 auto', lineHeight: 1.7 }}>{t('From listening to resolution, every layer is built to help your brand move faster, sound better, and care deeper — at scale.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="holistic-digital-care-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
